/* The ticker. The one piece of GeoCities inheritance in this design, and it is
 * kept because it is the correct object for a radio station — a strip of live
 * transmission data — not because it is a nostalgia joke.
 *
 * WHAT GOES IN IT IS THE ENTIRE POINT. It carries what is on air, who is on
 * next, how many people are listening, how big the archive is, and whether the
 * open call is open. It never carries a slogan. A scrolling strip of "GOOD
 * VIBES ONLY" is decoration that costs a person with vestibular sensitivity a
 * headache; a scrolling strip of facts is the marquee doing the one job it was
 * ever good at.
 *
 * Three things it has to get right:
 *
 *  - The duplicate half of the track is aria-hidden. The marquee works by
 *    translating -50% across two copies of the same content, and without the
 *    hidden flag a screen reader reads the whole station twice.
 *  - There is a VISIBLE pause control. WCAG 2.2.2 requires a mechanism to
 *    pause anything that moves automatically for more than five seconds. This
 *    one moves for as long as the tab is open.
 *  - prefers-reduced-motion stops it dead and shows the items standing still.
 *    Not slower. Stopped.
 *
 * With no `items` it fetches its own data: /api/live on a 60s poll (the player
 * polls at 15s for the audio; the ticker is not worth that) plus one call for
 * the archive size. Pass `items` on any page that already holds live state.
 */

import { useEffect, useMemo, useState } from 'react';

import { api, isAbortError } from '../api';
import { formatTime, tzLabel } from '../format';
import type { TzMode } from '../format';
import type { LiveState } from '../types';
import { hostNames, slotHostNames, usePrefersReducedMotion } from './shared';

/** Long enough that the track always overruns a wide viewport before it loops. */
const MIN_TRACK_CHARS = 200;

const POLL_MS = 60_000;

export interface TickerProps {
  /**
   * The strip, already written. Every string is one item; the separators are
   * drawn by CSS. Leave it out and the ticker sources its own.
   */
  items?: string[];
  /** Bone on ink instead of the accent on ink — the off-air tone. */
  live?: boolean;
  /** Which clock the times are written in. Station time, and labelled. */
  tz?: TzMode;
  className?: string;
}

/** `NEXT 22:00 CEST · LOW CEILING W/ SOMBRA` */
function nextItem(state: LiveState, tz: TzMode): string | null {
  const next = state.next?.[0];
  if (!next) return null;
  const when = `${formatTime(next.starts_at, tz)} ${zoneWord(tz, next.starts_at)}`.trim();
  const who = slotHostNames(next.title, next.hosts);
  return `Next ${when} · ${next.title}${who ? ` w/ ${who}` : ''}`;
}

/** `CEST`, `UTC`, or nothing when the zone has no short name worth printing. */
function zoneWord(tz: TzMode, at?: string): string {
  const label = tzLabel(tz, at);
  if (label === 'UTC') return 'UTC';
  const parts = label.split('·');
  return parts.length > 1 ? parts[parts.length - 1].trim() : '';
}

/**
 * The station, in five or six facts. Written here rather than on each page so
 * that the strip says the same thing on every route.
 */
export function tickerItems(
  state: LiveState | null,
  archiveTotal: number | null,
  tz: TzMode = 'station',
): string[] {
  if (!state) return [];

  const items: string[] = [];

  if (state.live && state.now) {
    const who = slotHostNames(state.now.title, state.now.hosts);
    items.push(`On air — ${state.now.title}${who ? ` w/ ${who}` : ''}`);
    if (!state.now.dj_connected) items.push('Starting shortly — waiting on the booth');
  } else {
    items.push('Off air');
    const fallback = state.fallback;
    if (fallback) {
      const playing = fallback.playing;
      const who = playing ? hostNames(playing.hosts) : '';
      items.push(
        playing
          ? `Now playing: ${fallback.label} — ${playing.title}${who ? ` w/ ${who}` : ''}`
          : `Now playing: ${fallback.label}`,
      );
    }
  }

  const next = nextItem(state, tz);
  if (next) items.push(next);

  if (typeof archiveTotal === 'number') items.push(`${archiveTotal} sets in the archive`);
  items.push('Submissions open — send a link, not a file');

  return items;
}

/** Repeat until the strip is long enough to overrun a wide viewport. */
function fill(items: string[]): string[] {
  if (items.length === 0) return items;
  const length = items.join('').length;
  const copies = Math.max(1, Math.ceil(MIN_TRACK_CHARS / Math.max(1, length)));
  const out: string[] = [];
  for (let i = 0; i < copies; i += 1) out.push(...items);
  return out;
}

export function Ticker({ items, live, tz = 'station', className }: TickerProps) {
  const reduced = usePrefersReducedMotion();
  const [paused, setPaused] = useState(false);

  const [state, setState] = useState<LiveState | null>(null);
  const [archiveTotal, setArchiveTotal] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);

  const selfSourced = items === undefined;

  /* The archive size changes once a week. Asked for once, with limit=1 so the
     station is not sending 24 episode cards to fill in one number. */
  useEffect(() => {
    if (!selfSourced) return;
    const controller = new AbortController();
    api
      .getEpisodes({ limit: 1 }, controller.signal)
      .then((page) => setArchiveTotal(page.total))
      .catch(() => {
        /* The strip is fine without it. Not worth a word on screen. */
      });
    return () => controller.abort();
  }, [selfSourced]);

  useEffect(() => {
    if (!selfSourced) return;

    let controller: AbortController | null = null;
    let cancelled = false;

    const poll = () => {
      controller?.abort();
      controller = new AbortController();
      api
        .getLive(controller.signal)
        .then((next) => {
          if (cancelled) return;
          setState(next);
          setFailed(false);
        })
        .catch((error: unknown) => {
          if (cancelled || isAbortError(error)) return;
          setFailed(true);
        });
    };

    poll();
    const timer = window.setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      controller?.abort();
    };
  }, [selfSourced]);

  const resolved = useMemo(() => {
    if (items) return items;
    if (state) return tickerItems(state, archiveTotal, tz);
    if (failed) return ['We cannot reach the station right now — the stream may still be running'];
    return ['Connecting to the station'];
  }, [items, state, archiveTotal, failed, tz]);

  const isLive = live ?? (state ? state.live : true);
  const moving = resolved.length > 0 && !reduced && !(state === null && selfSourced && !failed);

  const classes = ['ticker', isLive ? null : 'ticker--off', className].filter(Boolean).join(' ');

  if (resolved.length === 0) return null;

  if (!moving) {
    /* Reduced motion, or nothing to scroll yet. The items stand still and the
       pause control goes away with the movement it was there to stop. */
    return (
      <div className={classes} role="region" aria-label="Station updates">
        <div className="ticker__win">
          <div className="ticker__track ticker__track--still">
            {resolved.map((item, index) => (
              <span className="ticker__item mono-13" key={`${index}-${item}`}>
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const filled = fill(resolved);
  const track = [...filled, ...filled];

  return (
    <div
      className={classes}
      role="region"
      aria-label="Station updates"
      data-paused={paused ? 'true' : 'false'}
    >
      {/* The track is twice as wide as its content and overflows on purpose;
          the window is what keeps it from shoving the pause control off the
          right-hand edge of the strip. */}
      <div className="ticker__win">
        <div className="ticker__track">
          {track.map((item, index) => (
            <span
              className="ticker__item mono-13"
              key={`${index}-${item}`}
              /* Only the first pass is readable. Everything after it is the
                 same sentence again, for the loop. */
              aria-hidden={index >= resolved.length ? true : undefined}
            >
              {item}
            </span>
          ))}
        </div>
      </div>

      <button
        type="button"
        className="ticker__pause mono-13"
        aria-pressed={paused}
        onClick={() => setPaused((was) => !was)}
      >
        <span className="ticker__pauseglyph" aria-hidden="true">
          {paused ? '▶' : '❚❚'}
        </span>
        {paused ? 'Play' : 'Pause'}
        <span className="vh"> the station updates strip</span>
      </button>
    </div>
  );
}
